import React, { useEffect, useState, useRef } from 'react';

interface StatItem {
  number: number;
  label: string;
  suffix?: string;
}

const Stats: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [counts, setCounts] = useState([50, 12, 4, 98]); // Initialize with actual numbers
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const timersRef = useRef<NodeJS.Timeout[]>([]);
  const hasAnimatedRef = useRef(false);

  const stats: StatItem[] = [
    { number: 50, label: 'Projects Completed', suffix: '+' },
    { number: 12, label: 'Active Projects', suffix: '+' },
    { number: 4, label: 'Awards Won', suffix: '' },
    { number: 98, label: 'Client Satisfaction', suffix: '%' }
  ];

  const animateNumber = (index: number) => {
    const stat = stats[index];
    const duration = 800; // Fast animation
    const steps = 60;
    const stepDuration = duration / steps;
    const increment = stat.number / steps;
    let current = 0;

    // Clear any existing timer for this index
    if (timersRef.current[index]) {
      clearInterval(timersRef.current[index]);
    }

    const timer = setInterval(() => {
      current += increment;
      if (current >= stat.number) {
        current = stat.number;
        clearInterval(timer);
        timersRef.current[index] = null as any;
      }

      setCounts(prev => {
        const newCounts = [...prev];
        newCounts[index] = Math.floor(current);
        return newCounts;
      });
    }, stepDuration);

    timersRef.current[index] = timer;
  };

  const handleMouseEnter = (index: number) => {
    setHoveredIndex(index);
    animateNumber(index);
  };

  const handleMouseLeave = (index: number) => {
    setHoveredIndex(null);
    // Reset to original number after a short delay
    setTimeout(() => {
      setCounts(prev => {
        const newCounts = [...prev];
        newCounts[index] = stats[index].number;
        return newCounts;
      });
    }, 1000);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Trigger animation every time the section comes into view
        if (entry.isIntersecting) {
          setIsVisible(true);
          hasAnimatedRef.current = false; // Reset so animation can play again
        } else {
          setIsVisible(false);
        }
      },
      { threshold: 0.2 } // Trigger when 20% of section is visible
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible || hasAnimatedRef.current) return;

    const duration = 500; // Faster animation - 1.5 seconds
    const steps = 60; // 60fps
    const stepDuration = duration / steps;

    // Clear any existing timers
    timersRef.current.forEach(timer => clearInterval(timer));
    timersRef.current = [];

    // Reset counts to 0 before starting animation
    setCounts([0, 0, 0, 0]);

    // Add a small delay to ensure state is reset
    setTimeout(() => {
      stats.forEach((stat, index) => {
        const increment = stat.number / steps;
        let current = 0;

        const timer = setInterval(() => {
          current += increment;
          if (current >= stat.number) {
            current = stat.number;
            clearInterval(timer);
            // Remove this timer from the array
            timersRef.current = timersRef.current.filter(t => t !== timer);
          }

          setCounts(prev => {
            const newCounts = [...prev];
            newCounts[index] = Math.floor(current);
            return newCounts;
          });
        }, stepDuration);

        timersRef.current.push(timer);
      });
    }, 100); // Small delay to ensure proper initialization

    hasAnimatedRef.current = true;

    // Cleanup function
    return () => {
      timersRef.current.forEach(timer => clearInterval(timer));
      timersRef.current = [];
    };
  }, [isVisible, stats]);

  return (
    <section ref={sectionRef} id="stats" className="py-4 dark:bg-navy-light light:bg-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="text-center"
              style={{ transitionDelay: `${index * 0.2}s` }}
              onMouseEnter={() => handleMouseEnter(index)}
              onMouseLeave={() => handleMouseLeave(index)}
            >
              <div className="glass rounded-2xl p-8 hover:bg-opacity-20 transition-all duration-300 group cursor-pointer">
                <div className="text-4xl lg:text-5xl font-bold text-accent mb-2 group-hover:scale-110 transition-transform duration-300">
                  {counts[index]}{stat.suffix}
                </div>
                <div className="dark:text-gray-300 light:text-gray-600 text-sm lg:text-base font-medium">
                  {stat.label}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Stats;
